import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { GetReportShifts } from "./../../api/report";
import { GetAcativeRestaurants } from "./../../api/restaurant";

function getMonthStartDate() {
  const date = new Date();
  date.setDate(1);
  return date.toISOString().slice(0, 10);
}

function getTodayDate() {
  return new Date().toISOString().slice(0, 10);
}

function hoursInWindow(start, end, periodStart, periodEnd) {
  let shiftEnd = end;
  if (shiftEnd < start) {
    shiftEnd += 24;
  }
  const intersectionStart = Math.max(start, periodStart);
  const intersectionEnd = Math.min(shiftEnd, periodEnd);
  if (intersectionStart < intersectionEnd) {
    return intersectionEnd - intersectionStart;
  }
  return 0;
}

function ratioPercent(part, total) {
  if (!total) {
    return null;
  }
  return (part / total) * 100;
}

function deviationPercent(fact, plan) {
  if (!plan) {
    return null;
  }
  return (fact / plan - 1) * 100;
}

function deviationClass(value) {
  if (value > 0) {
    return "report-shifts-over";
  }
  if (value < 0) {
    return "report-shifts-under";
  }
  return "";
}

function formatHours(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return Number(value).toFixed(1);
}

function formatPercent(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  return `${Number(value).toFixed(1)}%`;
}

function formatSignedPercent(value) {
  if (value == null || Number.isNaN(Number(value))) {
    return "—";
  }
  const formatted = `${Number(value).toFixed(1)}%`;
  return value > 0 ? `+${formatted}` : formatted;
}

function buildRestaurantRows(payload) {
  const shifts = payload.shifts || [];
  const templateShifts = payload.template_shifts || [];
  const fts = payload.fts || [];
  const users = payload.users || [];
  const rests = payload.restaurants || {};
  const bkHoursByRestaurant = payload.bk_hours || {};

  const restaurantIds = [];
  for (const item of templateShifts) {
    if (!restaurantIds.includes(item.restaurant_id)) {
      restaurantIds.push(item.restaurant_id);
    }
  }

  return restaurantIds.map((rest) => {
    let bkHours = Number(bkHoursByRestaurant[rest] || 0);
    let sHours = 0;
    let tsHours = 0;
    let sCount = 0;
    let tsCount = 0;
    let ftCount = 0;
    let sPeriod = 0;
    let sPeriodTs = 0;
    let usersRest = 0;

    for (const userRestaurantId of users) {
      if (Number(userRestaurantId) === Number(rest)) {
        usersRest += 1;
      }
    }

    for (const template of templateShifts) {
      if (Number(template.restaurant_id) !== Number(rest)) {
        continue;
      }
      tsHours += template.hours;
      tsCount += 1;
      sPeriodTs += hoursInWindow(template.start, template.end, 12, 20);
    }

    for (const shift of shifts) {
      if (
        Number(shift.restaurant_id) !== Number(rest) ||
        shift.template_shift_id === null ||
        shift.template_shift_id === undefined
      ) {
        continue;
      }
      sHours += shift.hours;
      sCount += 1;
      sPeriod += hoursInWindow(shift.start, shift.end, 12, 20);
    }

    for (const fullTime of fts) {
      if (Number(fullTime.restaurant_id) === Number(rest)) {
        ftCount += 1;
      }
    }

    return {
      id: rest,
      name: rests[rest] || `Ресторан ${rest}`,
      bkHours,
      ftrFact: ratioPercent(bkHours, sHours),
      ftrPlanShifts: ratioPercent(sCount, tsCount),
      ftrPlanHours: ratioPercent(sHours, tsHours),
      ftCount,
      ftShare: usersRest ? ratioPercent(ftCount, usersRest) : null,
      usersRest,
      shifts: {
        plan: tsCount,
        fact: sCount,
        diff: sCount - tsCount,
        percent: deviationPercent(sCount, tsCount),
      },
      hours: {
        plan: tsHours,
        fact: sHours,
        diff: sHours - tsHours,
        percent: deviationPercent(sHours, tsHours),
      },
      period: {
        hours: sPeriod,
        percent: ratioPercent(sPeriod, sPeriodTs),
      },
    };
  });
}

function MetricRow({ label, row, planFormat = "int" }) {
  const formatPlan =
    planFormat === "hours" ? formatHours(row.plan) : String(row.plan);
  const formatFact =
    planFormat === "hours" ? formatHours(row.fact) : String(row.fact);
  const formatDiff =
    planFormat === "hours" ? formatHours(row.diff) : String(row.diff);

  return (
    <tr>
      <th scope="row">{label}</th>
      <td className="text-center">{formatPlan}</td>
      <td className="text-center">{formatFact}</td>
      <td className={`text-center ${deviationClass(row.diff)}`}>{formatDiff}</td>
      <td className={`text-center ${deviationClass(row.percent)}`}>
        {formatSignedPercent(row.percent)}
      </td>
    </tr>
  );
}

const ReportShifts = ({ token }) => {
  const [startDate, setStartDate] = useState(getMonthStartDate);
  const [endDate, setEndDate] = useState(getTodayDate);
  const [restaurantId, setRestaurantId] = useState(0);
  const [restaurantList, setRestaurantList] = useState([]);
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    async function loadRestaurants() {
      try {
        const restaurants = await GetAcativeRestaurants(token);
        if (Array.isArray(restaurants)) {
          setRestaurantList(restaurants);
        }
      } catch (err) {
        console.error(err);
      }
    }
    loadRestaurants();
  }, [token]);

  async function loadReport(event) {
    event?.preventDefault();
    if (!startDate || !endDate) {
      setError("Укажите начало и конец периода");
      return;
    }
    if (startDate > endDate) {
      setError("Начало периода не может быть позже конца");
      return;
    }

    setLoading(true);
    setError("");
    const response = await GetReportShifts(
      token,
      startDate,
      endDate,
      restaurantId,
    );
    setLoading(false);

    if (!response || response.detail) {
      const detail = response?.detail;
      setError(
        typeof detail === "string" ? detail : "Не удалось загрузить отчёт",
      );
      setRows(null);
      return;
    }
    if (!Array.isArray(response.template_shifts) || !response.template_shifts.length) {
      setRows([]);
      return;
    }
    setRows(buildRestaurantRows(response));
  }

  return (
    <div className="reports-page report-shifts-page">
      <div className="reports-header">
        <div>
          <h1>Отчет по сменам</h1>
          <p className="reports-hint">
            План и факт смен, часы по отметкам Boss Control
          </p>
        </div>
        <Link to="/reports" className="reports-back-link">
          ← К отчётам
        </Link>
      </div>

      <form className="users-filters report-shifts-filters" onSubmit={loadReport}>
        <label className="users-field">
          <span>Период с</span>
          <input
            className="form-control"
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
          />
        </label>
        <label className="users-field">
          <span>Период по</span>
          <input
            className="form-control"
            type="date"
            value={endDate}
            onChange={(event) => setEndDate(event.target.value)}
          />
        </label>
        <label className="users-field">
          <span>Ресторан</span>
          <select
            className="form-select"
            value={restaurantId}
            onChange={(event) => setRestaurantId(Number(event.target.value))}
          >
            <option value={0}>Все рестораны</option>
            {restaurantList.map((restaurant) => (
              <option key={restaurant.id} value={restaurant.id}>
                {restaurant.name}
              </option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          className="btn login-submit report-shifts-submit"
          disabled={loading}
        >
          {loading ? "Загрузка…" : "Показать"}
        </button>
      </form>

      {error ? <div className="login-error">{error}</div> : null}

      {loading && !rows ? (
        <div className="reports-loading">
          <div className="spinner-border profile-spinner" role="status">
            <span className="visually-hidden">Загрузка...</span>
          </div>
          <p>Считаем отчёт…</p>
        </div>
      ) : null}

      {rows && rows.length === 0 ? (
        <div className="reports-empty">
          За выбранный период нет плановых смен
        </div>
      ) : null}

      {rows && rows.length > 0
        ? rows.map((row) => (
            <article key={row.id} className="report-shifts-card">
              <div className="report-shifts-card-accent" />
              <div className="report-shifts-card-body">
                <header className="report-shifts-card-head">
                  <h2>{row.name}</h2>
                </header>

                <div className="chk-summary-kpis report-shifts-kpis">
                  <div className="chk-summary-kpi">
                    <span>Бк часы</span>
                    <strong>{formatHours(row.bkHours)}</strong>
                  </div>
                  <div className="chk-summary-kpi">
                    <span>FTR факт</span>
                    <strong
                      className={
                        row.ftrFact == null
                          ? ""
                          : deviationClass(row.ftrFact - 100)
                      }
                    >
                      {formatPercent(row.ftrFact)}
                    </strong>
                  </div>
                  <div className="chk-summary-kpi">
                    <span>FTR план период</span>
                    <strong>{formatPercent(row.ftrPlanHours)}</strong>
                    <em>смены {formatPercent(row.ftrPlanShifts)}</em>
                  </div>
                  <div className="chk-summary-kpi">
                    <span>Full Time</span>
                    <strong>{row.ftCount}</strong>
                    <em>
                      {row.usersRest
                        ? `${formatPercent(row.ftShare)} от ЧК`
                        : "Нет пользователей"}
                    </em>
                  </div>
                </div>

                <div className="report-shifts-table-wrap">
                  <table className="report-shifts-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th className="text-center">План</th>
                        <th className="text-center">Факт</th>
                        <th className="text-center">Не закрыты</th>
                        <th className="text-center">%</th>
                      </tr>
                    </thead>
                    <tbody>
                      <MetricRow label="Смены" row={row.shifts} />
                      <MetricRow
                        label="Часы"
                        row={row.hours}
                        planFormat="hours"
                      />
                    </tbody>
                  </table>
                </div>

                <div className="report-shifts-period">
                  <span>Часы с 12:00 до 20:00</span>
                  <strong>
                    {formatHours(row.period.hours)} ч
                    <em>{formatPercent(row.period.percent)} от плана</em>
                  </strong>
                </div>
              </div>
            </article>
          ))
        : null}
    </div>
  );
};

export default ReportShifts;
